import CalendarDailyDetails from "@/components/calendar/calendar-daily-details";
import { CalendarList } from "@/components/calendar/calendar-list";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Combobox from "@/components/core/forms/combobox";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller.tsx";
import { Content } from "@/components/layouts/content";
import { entityType } from "@/constants/entity-types.ts";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import courtBranchService from "@/features/court-detail/api/services/court-branches-service";
import { calendarsQueryOptions } from "@/features/dashboard/api/queries/calendar-queries.ts";
import { eventsQueryOptions } from "@/features/dashboard/api/queries/event-queries.ts";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import { useRef, useState } from "react";
import { useEffect } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/calendar-of-events")({
	component: Page,
	validateSearch: z.object({
		calendars: z.array(z.string()).optional(),
		date: z.string().optional(),
		view: z.boolean().default(false),
	}),
	loaderDeps: ({ search }) => ({
		calendars: search.calendars,
		date: search.date,
		view: search.view,
	}),
	loader: ({ context: { queryClient }, deps, params }) => {
		queryClient.ensureQueryData(
			calendarsQueryOptions({
				...params,
				entity_type: entityType.USER,
			}),
		);

		return queryClient.ensureQueryData(
			eventsQueryOptions({
				...deps,
				date: deps.date,
			}),
		);
	},
});

function Page() {
	const [eventCounts, setEventCounts] = useState({});
	const { user } = useAuth();
	const modal = useModal();
	const calendarRef = useRef<FullCalendar | null>(null);
	const search = Route.useSearch();
	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(user?.branches?.[0]?.assignable?.id);

	const calendarsQueries = useSuspenseQuery(
		calendarsQueryOptions({
			entity_type: entityType.USER,
			branch_id: selectedBranchId,
		}),
	);
	const calendars = calendarsQueries.data;
	const eventsQuery = useSuspenseQuery(
		eventsQueryOptions({
			calendars: isEmpty(search.calendars) && calendars.data.length !== 0 ? calendars.data.map((cal) => cal.id) : search.calendars,
			branch_id: selectedBranchId,
		}),
	);
	const events = eventsQuery.data;

	const [calendarView, setCalendarView] = useState(!search.view);
	const [date, setDate] = useState("");

	const handleDateClick = async (info: { dateStr: string }) => {
		setCalendarView((prev) => !prev);
		setDate(info.dateStr);
	};

	useEffect(() => {
		if (selectedBranchId) {
			calendarsQueries.refetch();
			eventsQuery.refetch();
		}
	}, [selectedBranchId, calendarsQueries.refetch, eventsQuery.refetch]);

	const loadBranchesOptions = async (value: string) => {
		const items = await courtBranchService.list({
			"filter[search_branch]": value,
		});

		return items.data.map((item) => ({
			label: item.formatted_name,
			value: item.id,
		}));
	};

	// useEffect(() => {
	// 	if (search.date !== null || search.date !== "") {
	// 		setDate(search.date || "");
	// 	}
	// }, [search.date]);

	return (
		<Content>
			<div className="flex flex-row w-full h-full px-2">
				{/* <div className="realtive inline-flex flex-col shrink-0 w-[380px]">
					<div className="fixed left-0 bottom-0 top-[50px] w-[380px] border-r border-border/50">
						<ScrollArea>
							<div className="flex flex-col w-full h-[calc(100svh-50px)]">
								<CalendarList />
							</div>
						</ScrollArea>
					</div>
				</div> */}
				<div className="flex flex-col w-full">
					<div className={clsx("flex w-[300px] self-end", calendarView ? "mt-20" : "mt-5")}>
						<Combobox
							onValueChange={(value) => {
								setSelectedBranchId(value as string);
								// console.log("branch", value);
								// setSelectedBranch(value as string);
								// fetchStatistics(selectedYear as string, value as string);
							}}
							clearable={false}
							placeholderValue={{
								label: user?.branches?.[0]?.assignable?.name,
								value: user?.branches?.[0]?.assignable?.id,
							}}
							defaultValue={user?.branches?.[0]?.assignable?.id}
							loadOptions={loadBranchesOptions}
						/>
					</div>
					{calendarView ? (
						<div className="custom-full-calendar flex flex-col w-full">
							<FullCalendar
								dayCellContent={(args) => {
									const currentDate = args.date.toDateString();
									// const counts = events.data.filter((e) => e.start && new Date(e.start).toDateString() === currentDate && e.type !== "holiday").length;
									const counts = events.data.filter((e) => e.start && new Date(e.start).toDateString() === currentDate).length;

									return (
										<div className="">
											<div className="absolute bottom-1 left-1 text-sm font-medium text-gray-900">{args.dayNumberText}</div>
											{counts > 0 && (
												<div className="absolute top-1 left-1 text-[0.55rem] px-2 py-1 rounded-full bg-indigo-100 text-indigo-900 font-medium whitespace-nowrap z-10 ml-11">
													{counts} event{counts > 1 ? "s" : ""}
												</div>
											)}
										</div>
									);
								}}
								ref={calendarRef}
								schedulerLicenseKey="XXX"
								initialView="dayGridMonth"
								stickyHeaderDates={false}
								plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
								eventColor="#797A7E"
								events={events.data}
								dateClick={handleDateClick}
								headerToolbar={{
									start: "prev,next todayHighlight",
									center: "title",
									end: "dayGridMonth,dayGridWeek",
								}}
								buttonText={{
									dayGridMonth: "Month",
									dayGridWeek: "Week",
								}}
								customButtons={{
									todayHighlight: {
										text: "Today",
										click: () => calendarRef.current?.getApi().today(),
									},
								}}
								// dayCellDidMount={(info) => {
								// 	const currentDate = info.date.toDateString();
								// 	const counts = events.data.filter(
								// 		(e) =>
								// 			e.start &&
								// 			new Date(e.start).toDateString() === currentDate &&
								// 			e.type !== "holiday"
								// 	).length;

								// 	if (counts > 0) {
								// 		const wrapper = document.createElement("div");
								// 		wrapper.style.display = "flex";
								// 		wrapper.style.justifyContent = "center";
								// 		wrapper.style.marginTop = "4px";

								// 		const countDiv = document.createElement("div");
								// 		countDiv.innerText = `${counts} event${counts > 1 ? "s" : ""}`;
								// 		countDiv.style.fontSize = "0.65rem";
								// 		countDiv.style.padding = "2px 8px";
								// 		countDiv.style.borderRadius = "999px";
								// 		countDiv.style.backgroundColor = "#e0e7ff";
								// 		countDiv.style.color = "#1e3a8a";
								// 		countDiv.style.fontWeight = "500";
								// 		countDiv.style.whiteSpace = "nowrap";
								// 		countDiv.style.position = "absolute";

								// 		wrapper.appendChild(countDiv);
								// 		info.el.appendChild(wrapper);
								// 	}
								// }}

								eventContent={({ event }) => {
									const totalEvents = event.extendedProps.totalEvents || 0;
									const color = event._def?.ui?.backgroundColor;
									return (
										<div
											className="flex items-center py-1 px-1.5 gap-1 rounded cursor-pointer overflow-hidden w-full"
											style={{ backgroundColor: color, color: "white" }}
										>
											{/* <button type="button" className="font-semibold truncate">

											{totalEvents} {event.title}
											</button>  */}

											<>
												{event.extendedProps.type !== "holiday" && (
													<span className="font-normal text-right">
														{new Date(event.start).toLocaleTimeString([], {
															hour: "2-digit",
															minute: "2-digit",
														})}
													</span>
												)}
												<button type="button" className="font-semibold truncate">
													{event.title}
												</button>
											</>
										</div>
									);
								}}
								eventClick={async (info) => {
									const caseRecord = info.event.extendedProps.caseRecord;

									if (caseRecord) {
										const records = Array.isArray(caseRecord) ? caseRecord : [caseRecord];

										modal.openModal({
											title: info.event.title,
											size: "lg",
											component: <EventTable records={records} />,
										});
									}
								}}
							/>
						</div>
					) : (
						<CalendarDailyDetails date={date} calendarView={calendarView} handleDateClick={handleDateClick} />
					)}
				</div>
			</div>
		</Content>
	);
}

function EventTable({ records }: { records: CaseRecord[] }) {
	const { closeModal } = useModalControl();
	const { user } = useAuth();
	const isOcc = user?.is_occ;

	const columns: ColumnDef<CaseRecord>[] = [
		{
			id: "name",
			label: "Case Title",
			cell: ({ data }) =>
				isOcc ? (
					<Link onClick={closeModal} className="text-blue-600" to="/case-classification/$caseClassificationId" params={{ caseClassificationId: data?.id }}>
						Case {data?.case_title}
					</Link>
				) : (
					<Link onClick={closeModal} className="text-blue-600" to="/case-evaluation/$caseEvaluationId" params={{ caseEvaluationId: data?.id }}>
						Case {data?.case_title}
					</Link>
				),
		},
		{
			id: "case_number",
			label: "Case Number",
			cell: ({ data }) => data.case_number,
		},
		{
			id: "name",
			label: "Date Filed",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => formatDate(data?.filed_at),
		},
	];

	return <DataTable columns={columns} data={records} />;
}
